import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import translations from "@/i18n/translations";

const languages = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "he", label: "עברית", flag: "🇮🇱" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
] as const;

export type LangCode = (typeof languages)[number]["code"];

interface LangContextType {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  languages: typeof languages;
  t: (key: string) => string;
}

const LangContext = createContext<LangContextType>({
  lang: "en",
  setLang: () => {},
  languages,
  t: (key) => key,
});

export const useLang = () => useContext(LangContext);

export const LangProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<LangCode>(
    () => (localStorage.getItem("thefloor-lang") as LangCode) || "en"
  );

  const rtlLangs = ["ar", "he"];

  const setLang = useCallback((code: LangCode) => {
    setLangState(code);
    localStorage.setItem("thefloor-lang", code);
    document.documentElement.dir = rtlLangs.includes(code) ? "rtl" : "ltr";
  }, []);

  useEffect(() => {
    document.documentElement.dir = rtlLangs.includes(lang) ? "rtl" : "ltr";
  }, []);

  const t = useCallback(
    (key: string): string => translations[lang]?.[key] || translations.en[key] || key,
    [lang]
  );

  return (
    <LangContext.Provider value={{ lang, setLang, languages, t }}>
      {children}
    </LangContext.Provider>
  );
};
